<template>
  <div>
    <BTableSimple
      striped
      responsive
    >
      <TableHeader
        :is-checked-all="isCheckedAll"
        :is-disabled="isCheckedAllDisabled"
        :is-indeterminate="isCheckedAllDisabled || isCheckedAllIndeterminate"
        @toggle-select="handleToggleCheckedAll"
      />
      <BTbody class="[&_td]:align-middle [&_td]:text-capitalize [&_td]:text-[#333333] [&_td]:!text-[14px] [&_td]:!bg-white">
        <b-tr
          v-if="items.length === 0"
        >
          <b-td
            colspan="100%"
            class="text-center"
          >
            Tidak ada data yang dapat ditampilkan.
          </b-td>
        </b-tr>
        <TableRow
          v-for="row in flattenedRows"
          v-else
          :ref="`row-${row.orderIndex}`"
          :key="row.key"
          :row="row"
          :is-row-checked="isRowCheckedAll"
          :is-checked-all="isCheckedAll"
          @toggle-select="toggleSelect"
        />
      </BTbody>
    </BTableSimple>
  </div>
</template>

<script>
import TableHeader from './TableHeader.vue'
import TableRow from './TableRow.vue'

export default {
  name: 'Table',
  components: {
    TableHeader,
    TableRow,
  },
  props: {
    batchStatus: {
      type: String,
      default: 'Belum Dipacking',
    },
    items: {
      type: Array,
      default: () => [],
    },
    grandTotalOrder: {
      type: Number,
      default: 0,
    },
  },
  data() {
    return {
      packingMaterialsFetcher: undefined,
      isCheckedAllIndeterminate: false,
      isCheckedAll: false,
      isRowCheckedAll: false,
    }
  },
  computed: {
    /**
     * Flattens nested order items into individual product rows for table rendering.
     * @returns {Object[]} Flattened row objects
     */
    flattenedRows() {
      const rows = []
      this.items.forEach((item, orderIndex) => {
        const products = item.products || []
        const rowspan = products.length

        products.forEach((product, productIndex) => {
          rows.push({
            key: `${orderIndex}-${productIndex}`,
            orderIndex,
            isFirstProduct: productIndex === 0,
            rowspan,
            status: item.status,
            totalOrder: item.total_order,
            packings: item.packings,
            productName: product.product_name,
            variantName: product.product_variant_name || '-',
            isBundling: product.is_bundling,
            bundles: product.product_bundles || [],
            totalItem: product.total_item,
          })
        })
      })
      return rows
    },
    isAllShipped() {
      if (this.batchStatus !== 'Dikirim') {
        return true
      }
      return this.items.some(item => item.status !== 'Dikirim' && item.status !== 'Dibatalkan')
    },
    isCheckedAllDisabled() {
      return !this.isAllShipped || this.batchStatus === 'Dibatalkan'
    },
  },
  watch: {
    /**
     * Resets selection state when items data changes (e.g., API refresh).
     */
    items: {
      handler() {
        if (this.selected) {
          this.selected.clear()
          this.$emit('update-selected', [])
        }
      },
    },
  },
  created() {
    this.selected = new Set()
    this.$emit('update-selected', [])
  },
  beforeDestroy() {
    if (this.selected) {
      this.selected.clear()
      this.selected = null
    }
  },
  methods: {
    /**
     * Toggles selection state for a specific order.
     * @param {number} orderIndex - Index of the order to toggle
     */
    toggleSelect(orderIndex) {
      const item = this.items[orderIndex]
      if (item && (item.status === 'Dibatalkan' || item.status === 'Dikirim')) {
        return
      }

      const isCurrentlySelected = this.selected.has(orderIndex)
      const newSelectedState = !isCurrentlySelected

      if (newSelectedState) {
        this.selected.add(orderIndex)
      } else {
        if (this.isCheckedAll) {
          this.isCheckedAll = false
        }
        this.selected.delete(orderIndex)
      }

      if (!this.isCheckedAll) {
        this.isCheckedAllIndeterminate = this.selected.size === this.grandTotalOrder
      }

      this.$emit('update-selected', Array.from(this.selected))
    },
    /**
     * Checks if a product at given position is not selected.
     * @param {number} position - Order index to check
     * @returns {boolean} True if not selected
     */
    disabledProduct(position) {
      return !this.selected.has(position)
    },
    /**
     * Returns array of selected order indices.
     * @public Exposed for parent access via $refs
     * @returns {number[]} Selected order indices
     */
    getSelectedItems() {
      return Array.from(this.selected)
    },
    /**
     * Clears all current selections and updates all rows.
     * @public Exposed for parent access via $refs
     */
    clearSelection() {
      this.selected.clear()
      Object.keys(this.$refs).forEach(refKey => {
        if (refKey.startsWith('row-')) {
          const rows = this.$refs[refKey]
          if (rows && rows.length) {
            rows.forEach(row => row.forceUpdateCheckbox(false))
          }
        }
      })
      this.$emit('update-selected', [])
    },
    handleToggleCheckedAll(value) {
      this.isCheckedAll = value
      this.items.forEach((_, orderIndex) => {
        this.$refs[`row-${orderIndex}`][0].forceUpdateCheckbox(value)
      })
    },
  },
}
</script>
